import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Evidence-links surface (T-0034 / E04-F03 — bidirectional evidence graph).
 *
 * Backend mounts the read paths under
 * `/api/v1/engagements/:engagement_id/evidence-links` and branches on the
 * present query params:
 *
 *   - source-side (entry → docs)
 *       ?source_type=register_entry|rfi&source_id=<uuid>
 *
 *   - target-side (doc → citing-entries)
 *       ?target_document_id=<uuid>&page=<n>         (page passage shape)
 *       ?target_document_id=<uuid>&sheet_name=…&cell_address=…
 *                                                   (excel-cell shape)
 *
 * The evidence-graph modal (T-0116 misc-modals) walks the source-side first
 * (the outbound "this cites…" half of the graph). Reaching the inbound
 * half requires a target-side fetch per cited document — that's an
 * `N` request fan-out today; flagged as a backend follow-up for a
 * future composite "cited-by" endpoint.
 *
 * Shapes mirror `apps/api/src/services/evidence-links.ts → EvidenceLinkBy
 * Source/TargetItem`.
 */

export const EvidenceLinkBySourceItemSchema = z.object({
  id: z.string().uuid(),
  target_document_id: z.string().uuid(),
  document_title: z.string(),
  target_kind: z.string(),
  page: z.number().nullable(),
  section_path: z.string().nullable(),
  passage_offset: z.unknown(),
  sheet_name: z.string().nullable(),
  cell_address: z.string().nullable(),
  source_field_id: z.string().nullable(),
  created_at: z.string(),
});
export type EvidenceLinkBySourceItem = z.infer<typeof EvidenceLinkBySourceItemSchema>;

export const EvidenceLinkBySourceListSchema = z.object({
  items: z.array(EvidenceLinkBySourceItemSchema),
});
export type EvidenceLinkBySourceList = z.infer<typeof EvidenceLinkBySourceListSchema>;

export const EvidenceLinkByTargetItemSchema = z.object({
  id: z.string().uuid(),
  source_type: z.string(),
  source_id: z.string().uuid(),
  source_field_id: z.string().nullable(),
  target_kind: z.string(),
  page: z.number().nullable(),
  section_path: z.string().nullable(),
  sheet_name: z.string().nullable(),
  cell_address: z.string().nullable(),
  created_at: z.string(),
});
export type EvidenceLinkByTargetItem = z.infer<typeof EvidenceLinkByTargetItemSchema>;

export const EvidenceLinkByTargetListSchema = z.object({
  items: z.array(EvidenceLinkByTargetItemSchema),
});
export type EvidenceLinkByTargetList = z.infer<typeof EvidenceLinkByTargetListSchema>;

export type EvidenceSourceType = 'register_entry' | 'rfi';

export function listEvidenceLinksBySource(
  engagementId: string,
  sourceType: EvidenceSourceType,
  sourceId: string,
): Promise<EvidenceLinkBySourceList> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/evidence-links`,
    EvidenceLinkBySourceListSchema,
    {
      query: { source_type: sourceType, source_id: sourceId },
    },
  );
}

export function useEvidenceLinksBySourceQuery(
  engagementId: string | undefined,
  sourceType: EvidenceSourceType | undefined,
  sourceId: string | undefined,
  opts: { enabled?: boolean } = {},
) {
  const enabled =
    opts.enabled !== false && Boolean(engagementId) && Boolean(sourceType) && Boolean(sourceId);
  return useQuery({
    queryKey: ['evidence-links', 'by-source', engagementId, sourceType, sourceId] as const,
    queryFn: () => listEvidenceLinksBySource(engagementId!, sourceType!, sourceId!),
    enabled,
    staleTime: 30_000,
  });
}

export interface ListEvidenceLinksByTargetOpts {
  page?: number;
  sheet_name?: string;
  cell_address?: string;
}

export function listEvidenceLinksByTarget(
  engagementId: string,
  targetDocumentId: string,
  opts: ListEvidenceLinksByTargetOpts,
): Promise<EvidenceLinkByTargetList> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/evidence-links`,
    EvidenceLinkByTargetListSchema,
    {
      query: {
        target_document_id: targetDocumentId,
        ...opts,
      } as Record<string, string | number | boolean | undefined>,
    },
  );
}

export function useEvidenceLinksByTargetQuery(
  engagementId: string | undefined,
  targetDocumentId: string | undefined,
  opts: ListEvidenceLinksByTargetOpts,
  queryOpts: { enabled?: boolean } = {},
) {
  const enabled = queryOpts.enabled !== false && Boolean(engagementId) && Boolean(targetDocumentId);
  return useQuery({
    queryKey: ['evidence-links', 'by-target', engagementId, targetDocumentId, opts] as const,
    queryFn: () => listEvidenceLinksByTarget(engagementId!, targetDocumentId!, opts),
    enabled,
    staleTime: 30_000,
  });
}
